/** Sucursal convencional sugerida para eCheq: no tienen sucursal física. */
export const NRO_SUCURSAL_ECHEQ = 999;

export function esChequeEcheq(nroSucursal?: number | null): boolean {
  return Number(nroSucursal) === NRO_SUCURSAL_ECHEQ;
}

export const ETIQUETA_ECHEQ = 'eCheq';
export const ETIQUETA_ELECTRONICO = 'Electrónico';
export const ETIQUETA_FISICO = 'Físico';

/**
 * Fuente de verdad: esElectronico del API.
 * Fallback: sucursal 999 (datos históricos / clientes viejos).
 */
export function esChequeElectronico(cheque: {
  esElectronico?: boolean | null;
  nroSucursal?: number | null;
}): boolean {
  if (typeof cheque.esElectronico === 'boolean') return cheque.esElectronico;
  return esChequeEcheq(cheque.nroSucursal);
}

export function etiquetaTipoSoporteCheque(cheque: {
  esElectronico?: boolean | null;
  nroSucursal?: number | null;
}): string {
  return esChequeElectronico(cheque) ? ETIQUETA_ELECTRONICO : ETIQUETA_FISICO;
}

/**
 * Al marcar Electrónico: la sucursal queda fija en 999 (el eCheq no tiene sucursal física).
 * Al marcar Físico: limpiar 999 para forzar sucursal real.
 */
export function aplicarCambioTipoSoporteSucursal(
  esElectronico: boolean,
  nroSucursalActual?: number | null
): number | undefined {
  if (esElectronico) return NRO_SUCURSAL_ECHEQ;
  if (esChequeEcheq(nroSucursalActual)) return undefined;
  return nroSucursalActual == null ? undefined : Number(nroSucursalActual);
}

/** Sucursal a validar/enviar: en eCheq siempre 999, aunque el campo haya quedado vacío. */
export function resolverNroSucursalCheque(
  esElectronico: boolean | null | undefined,
  nroSucursal?: number | null
): number | undefined {
  if (esElectronico) return NRO_SUCURSAL_ECHEQ;
  return nroSucursal == null ? undefined : Number(nroSucursal);
}

export function etiquetaSucursalChequeTercero(
  nroSucursal?: number | null,
  nombreSucursal?: string | null,
  vacio = '-',
  esElectronico?: boolean | null
): string {
  if (typeof esElectronico === 'boolean' ? esElectronico : esChequeEcheq(nroSucursal)) {
    return ETIQUETA_ECHEQ;
  }
  const nombre = (nombreSucursal ?? '').trim();
  return nombre || vacio;
}

/**
 * Sucursal a enviar al API.
 * "Mostrar cheques físicos" no envía 999; el filtro de sucursal se envía tal cual.
 */
export function nroSucursalFiltroEcheq(
  soloChequesFisicos: boolean,
  nroSucursal?: string
): number | undefined {
  if (!nroSucursal) return undefined;
  const parsed = parseInt(nroSucursal, 10);
  if (!Number.isFinite(parsed)) return undefined;
  if (soloChequesFisicos && esChequeEcheq(parsed)) return undefined;
  return parsed;
}

/** Opciones de sucursal para el filtro: no mostrar eCheq. */
export function sucursalesOpcionesFiltroSinEcheq<T extends { nroSucursal: number; sucursal?: string }>(
  sucursales: T[]
): T[] {
  return sucursales.filter((s) => {
    if (esChequeEcheq(s.nroSucursal)) return false;
    return (s.sucursal ?? '').trim().toUpperCase() !== ETIQUETA_ECHEQ.toUpperCase();
  });
}

/** Con "Mostrar cheques físicos": no listar eCheqs. Con "solo eCheq": solo eCheqs. */
export function excluirEcheqsSiFiltroSucursal<T extends { nroSucursal?: number | null }>(
  cheques: T[],
  soloChequesFisicos: boolean,
  nroSucursal?: string,
  soloEcheq = false
): T[] {
  if (soloEcheq) {
    return cheques.filter((c) => esChequeEcheq(c.nroSucursal));
  }
  if (soloChequesFisicos) {
    return cheques.filter((c) => !esChequeEcheq(c.nroSucursal));
  }
  const suc = nroSucursalFiltroEcheq(false, nroSucursal);
  if (esChequeEcheq(suc)) {
    return cheques.filter((c) => !esChequeEcheq(c.nroSucursal));
  }
  return cheques;
}

export function aplicarParamsFiltroSucursalEcheq(
  params: Record<string, unknown>,
  soloChequesFisicos: boolean,
  nroSucursal?: string,
  soloEcheq = false
): void {
  if (soloEcheq) {
    params.esElectronico = true;
    params.esEcheq = true;
    return;
  }
  const suc = nroSucursalFiltroEcheq(soloChequesFisicos, nroSucursal);
  if (suc != null) params.nroSucursal = suc;
  if (soloChequesFisicos) {
    params.esElectronico = false;
    params.esEcheq = false;
  }
}

export function tieneFechaSalidaEfectiva(fechaSalida?: string | null): boolean {
  if (!fechaSalida) return false;
  const trimmed = fechaSalida.trim();
  if (!trimmed) return false;
  const year = parseInt(trimmed.substring(0, 4), 10);
  return !Number.isNaN(year) && year >= 1901;
}

export function esChequeTerceroAnulado(cheque: {
  senial?: number | null;
  concepto?: string | null;
}): boolean {
  if (cheque.senial === 0) return true;
  const concepto = (cheque.concepto ?? '').toUpperCase();
  return concepto.includes('ANULADO');
}

export function esChequeTerceroRechazado(cheque: {
  rechazado?: number | null;
}): boolean {
  return Number(cheque.rechazado ?? 0) > 0;
}

export type EstadoChequeTercero = 'anulado' | 'rechazado' | 'conciliado' | 'entregado' | 'en_cartera';

export function chequeTieneSalidaRegistrada(cheque: {
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
}): boolean {
  if (tieneFechaSalidaEfectiva(cheque.fechaSalida)) return true;
  const nroSalida = Number(cheque.nroMovCajaSalida ?? 0);
  if (Number.isFinite(nroSalida) && nroSalida > 0) return true;
  return (cheque.comprobanteSalida ?? '').trim().length > 0;
}

export function resolverEstadoChequeTercero(cheque: {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
  rechazado?: number | null;
}): EstadoChequeTercero {
  if (esChequeTerceroAnulado(cheque)) return 'anulado';
  if (esChequeTerceroRechazado(cheque)) return 'rechazado';
  if (cheque.conciliado) return 'conciliado';
  if (chequeTieneSalidaRegistrada(cheque)) return 'entregado';
  return 'en_cartera';
}

export function etiquetaEstadoChequeTercero(estado: EstadoChequeTercero): string {
  switch (estado) {
    case 'anulado':
      return 'Anulado';
    case 'rechazado':
      return 'Rechazado';
    case 'conciliado':
      return 'Conciliado';
    case 'entregado':
      return 'Entregado';
    default:
      return 'En cartera';
  }
}

export type ColorChipEstadoChequeTercero = 'default' | 'success' | 'warning' | 'info' | 'error';

export function colorChipEstadoChequeTercero(estado: EstadoChequeTercero): ColorChipEstadoChequeTercero {
  if (estado === 'anulado') return 'default';
  if (estado === 'rechazado') return 'error';
  if (estado === 'conciliado') return 'success';
  if (estado === 'entregado') return 'warning';
  return 'info';
}

export function etiquetaEstadoChequeTerceroConEcheq(cheque: {
  nroSucursal?: number | null;
  esElectronico?: boolean | null;
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
  rechazado?: number | null;
}): string {
  const estado = etiquetaEstadoChequeTercero(resolverEstadoChequeTercero(cheque));
  if (esChequeElectronico(cheque)) {
    return `${ETIQUETA_ECHEQ} · ${estado}`;
  }
  return estado;
}

/** Disponible para endoso / traspaso (no entregado, anulado, conciliado ni rechazado). */
export function esChequeDisponibleEnCartera(cheque: {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
  rechazado?: number | null;
}): boolean {
  return resolverEstadoChequeTercero(cheque) === 'en_cartera';
}

/** Se puede iniciar el flujo de rechazo (no anulado ni ya rechazado). */
export function esChequeRechazable(cheque: {
  senial?: number | null;
  concepto?: string | null;
  rechazado?: number | null;
}): boolean {
  if (esChequeTerceroAnulado(cheque)) return false;
  if (esChequeTerceroRechazado(cheque)) return false;
  return true;
}

type ChequeCarteraEstadoInput = {
  senial?: number | null;
  concepto?: string | null;
  conciliado?: number | null;
  fechaSalida?: string | null;
  nroMovCajaSalida?: number | null;
  comprobanteSalida?: string | null;
  rechazado?: number | null;
};

/** Filtro client-side del informe: con estado en_cartera, solo los realmente disponibles. */
export function filtrarChequesInformeCartera<T extends ChequeCarteraEstadoInput>(
  list: T[],
  estadoFiltro: string
): T[] {
  if (estadoFiltro.trim() === 'en_cartera') {
    return list.filter(esChequeDisponibleEnCartera);
  }
  return list;
}

/** Excluye cheques ya utilizados (traspaso/OP) y opcionalmente nroCarga en uso en la sesión. */
export function filtrarChequesDisponiblesParaUso<T extends ChequeCarteraEstadoInput & { nroCarga: number }>(
  list: T[],
  excluirNrosCarga?: ReadonlySet<number>
): T[] {
  return list.filter(
    (c) => esChequeDisponibleEnCartera(c) && !(excluirNrosCarga?.has(c.nroCarga) ?? false)
  );
}
